import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingUp, TrendingDown, DollarSign, Flame, Globe, Clock, Calendar, CalendarDays, Scale, Coins, Activity, Shuffle, AlertTriangle, ShieldCheck } from 'lucide-react';
import { apiGet } from '../../lib/apiClient';
import { API_ROUTES } from '../../lib/apiRoutes';

/**
 * SharedAnalysis page — renders the FULL EA Detailed Analysis (same as the modal)
 * by dynamically importing MyEAs and rendering EADetailModal in full-page mode.
 * 
 * Since the EADetailModal component has many internal dependencies inside MyEAs.js,
 * we use an approach where we redirect to a special "embedded" view that loads MyEAs
 * with the shared data injected.
 * 
 * APPROACH: This page fetches the shared data, then renders an inline <iframe-like> 
 * full-page version using the same data shape that EADetailModal expects.
 * We pass it to a lightweight wrapper that renders the full modal body without the 
 * modal overlay.
 */
export default function SharedAnalysis() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    apiGet(API_ROUTES.public.sharedAnalysis(token), { auth: false })
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-deepest, #080b12)' }}>
      <div className="text-center">
        <div className="w-10 h-10 border-2 border-accent-green border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-sm" style={{ color: 'var(--text-secondary, #8b949e)' }}>Loading shared analysis...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--bg-deepest, #080b12)' }}>
      <div className="text-center max-w-md">
        <AlertTriangle size={38} className="mx-auto mb-4 text-amber-400" />
        <h1 className="text-xl font-bold mb-2" style={{ color: 'var(--text-primary, #e6edf3)' }}>Link Unavailable</h1>
        <p className="text-sm mb-6" style={{ color: 'var(--text-secondary, #8b949e)' }}>{error}</p>
        <Link to="/" className="btn-primary text-sm">Go to TrackMyFxEA</Link>
      </div>
    </div>
  );

  // Render the full analysis page using the same data structure
  // We pass isPublic=true so AI Analyze is hidden
  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-deepest, #080b12)' }}>
      {/* Top Bar */}
      <div style={{ background: 'var(--bg-card, #161b22)', borderBottom: '1px solid var(--border-color, #21262d)', padding: '12px 24px', display: 'flex', gap: '16px', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 50 }}>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <Link to="/" style={{ color: '#00ff88', fontSize: '16px', fontWeight: 700, textDecoration: 'none' }}>TrackMyFxEA</Link>
          <Link to="/explore" style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '11px', fontWeight: 600, textDecoration: 'none' }}>Explore verified EAs</Link>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          {data.verification?.connector_verified && <span style={{ display: 'inline-flex', gap: '5px', alignItems: 'center', color: '#00ff88', background: '#00ff8815', border: '1px solid #00ff8840', borderRadius: '999px', padding: '5px 9px', fontSize: '10px', fontWeight: 700 }}><ShieldCheck size={12} /> {data.verification.label}</span>}
          <span style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '11px' }}>Shared EA analysis {data.shared_at ? `· ${new Date(data.shared_at).toLocaleDateString()}` : ''}</span>
        </div>
      </div>

      {/* Embedded Detail — uses the SharedDetailView which mirrors EADetailModal body exactly */}
      <SharedDetailView data={data} navigate={navigate} />
      {/* Footer CTA */}
      <div style={{ textAlign: 'center', padding: '24px 16px', borderTop: '1px solid var(--border-color, #21262d)' }}>
        <p style={{ color: 'var(--text-dim, #484f58)', fontSize: '11px', margin: '0 0 8px' }}>Powered by TrackMyFxEA · Professional EA performance analytics</p>
        <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}><Link to="/explore" style={{ color: '#60a5fa', fontSize: '12px', fontWeight: 600, textDecoration: 'none' }}>Explore verified EAs</Link><Link to="/register" style={{ color: '#00ff88', fontSize: '12px', fontWeight: 600, textDecoration: 'none' }}>Start tracking your EAs for free →</Link></div>
      </div>
    </div>
  );
}

/**
 * SharedDetailView — renders the EXACT SAME content as EADetailModal body
 * (everything inside the modal panel, minus the modal overlay and AI button).
 * 
 * This is a lazy-loaded dynamic import of the MyEAs page's internal components.
 * Since those aren't exported, we use the React.lazy approach with a wrapper.
 * 
 * ACTUALLY — to achieve TRUE 100% parity without refactoring MyEAs.js exports,
 * we render an iframe pointing to the app's own /shared-embed/:token route
 * which loads the dashboard layout with the detail pre-opened... 
 * 
 * NO — simplest: We just store the shared data in sessionStorage and redirect
 * to a special mode of MyEAs that reads it. BUT that's overengineered.
 * 
 * FINAL DECISION: The SharedAnalysis page will display data in its own full render
 * but will include ALL the same sections that the modal has. Let me render them all.
 */
function SharedDetailView({ data, navigate }) {
  const s = data.stats;
  const equityCurve = data.equity_curve || [];
  const ddChart = data.dd_chart || [];
  const floatChart = data.float_chart || [];
  const hourlyPerf = data.hourly_performance || [];
  const dailyPerf = data.daily_performance || [];
  const monthlyHeatmap = data.monthly_heatmap || [];
  const dailyHeatmap = data.daily_heatmap || [];
  const cashflows = data.cashflows || {};
  const costs = data.trading_costs || {};
  const sessionPerf = data.session_performance || {};
  const symbols = data.symbol_breakdown || [];
  const openTrades = data.open_trades || [];
  const totalClosedTrades = data.total_closed_trades || 0;

  const [ddType, setDdType] = useState('maximal');
  const [chartMode, setChartMode] = useState('DD');
  const chartData = chartMode === 'DD' ? ddChart : floatChart;

  return (
    <div style={{ maxWidth: '850px', margin: '0 auto', padding: '24px 16px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px', background: 'var(--bg-card, #161b22)', border: '1px solid var(--border-color, #21262d)', borderRadius: '12px', padding: '18px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div>
            <h1 style={{ color: 'var(--text-primary, #e6edf3)', fontSize: '20px', fontWeight: 700, margin: '0 0 8px' }}>{data.ea_name} - Detailed Analysis</h1>
            <p style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '12px', margin: 0 }}>Magic: {s.magic} · {totalClosedTrades} closed trades · Status: <span style={{ color: s.status === 'EXCELLENT' ? '#00ff88' : s.status === 'GOOD' ? '#378ADD' : s.status === 'POOR' ? '#ff4757' : '#f59e0b' }}>● {s.status}</span></p>
          </div>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {data.verification?.connector_verified && <span style={{ display: 'inline-flex', gap: '5px', alignItems: 'center', color: '#00ff88', background: '#00ff8815', border: '1px solid #00ff8840', borderRadius: '999px', padding: '5px 9px', fontSize: '10px', fontWeight: 700 }}><ShieldCheck size={12} /> Connector verified</span>}
            {data.account?.account_type && <span style={{ color: data.account.account_type === 'real' ? '#60a5fa' : '#f59e0b', background: data.account.account_type === 'real' ? '#3b82f615' : '#f59e0b15', border: `1px solid ${data.account.account_type === 'real' ? '#3b82f640' : '#f59e0b40'}`, borderRadius: '999px', padding: '5px 9px', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase' }}>{data.account.account_type}</span>}
          </div>
        </div>
        {data.account && <p style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '10px', margin: '12px 0 0' }}>{data.account.broker} · {data.account.platform} · {data.account.currency}{data.account.last_sync ? ` · Last synced ${new Date(data.account.last_sync).toLocaleString()}` : ''}</p>}
        {data.verification?.detail && <p style={{ color: 'var(--text-dim, #6e7681)', fontSize: '9px', lineHeight: 1.5, margin: '8px 0 0' }}>{data.verification.detail}</p>}
      </div>

      {/* === LIVE READINESS VERDICT === */}
      <LiveReadinessVerdictShared stats={s} totalClosedTrades={totalClosedTrades} />

      {/* Performance Header */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '10px', marginBottom: '24px' }}>
        <ModalStat label="Net Profit" value={`$${(s.net_profit||0).toLocaleString(undefined,{minimumFractionDigits:2})}`} sub={`(${s.winning_trades||0} wins)`} color={(s.net_profit||0) >= 0 ? '#00ff88' : '#ff4757'} />
        <ModalStat label="Win Rate" value={`${s.win_rate||0}%`} sub={`${s.winning_trades||0}W / ${s.losing_trades||0}L`} color={(s.win_rate||0) >= 50 ? '#00ff88' : '#ff4757'} />
        <ModalStat label="Profit Factor" value={s.profit_factor||0} sub="Risk/Reward" color="var(--text-primary, #e6edf3)" />
        <ModalStat label="Expectancy" value={`$${(s.expectancy||0).toFixed(2)}`} sub="Per trade" color={(s.expectancy||0) >= 0 ? '#00ff88' : '#ff4757'} />
        <ModalStat label="Total Trades" value={s.total_trades||0} sub="Completed" color="var(--text-primary, #e6edf3)" />
        <ModalStat label="Days Active" value={`${s.days_active||0}d`} sub="" color="#f59e0b" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px', marginBottom: '24px' }}>
        <ModalStat label="Current Balance" value={`$${(cashflows.current_balance||0).toLocaleString(undefined,{minimumFractionDigits:2})}`} sub="Live account balance" color="#a855f7" />
        <ModalStat label="Total Deposits" value={`$${(cashflows.total_deposits||0).toLocaleString(undefined,{minimumFractionDigits:2})}`} sub={`${cashflows.deposit_count||0} deposits`} color="#00ff88" />
        <ModalStat label="Total Withdrawals" value={`$${(cashflows.total_withdrawals||0).toLocaleString(undefined,{minimumFractionDigits:2})}`} sub={`${cashflows.withdrawal_count||0} withdrawals`} color="#ff4757" />
        <ModalStat label="Net Cashflow" value={`$${(cashflows.net_cashflow||0).toLocaleString(undefined,{minimumFractionDigits:2})}`} sub={`${cashflows.cashflow_count||0} cashflow records`} color={(cashflows.net_cashflow||0) >= 0 ? '#00ff88' : '#ff4757'} />
      </div>
      {/* Time-Based Comparison */}
      <SectionHeader title="Time-Based Comparison" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px', marginBottom: '24px' }}>
        <ModalStat label="Days Active" value={`${s.days_active||0}d`} color="#f59e0b" />
        <ModalStat label="Profit/Day" value={`$${(s.profit_per_day||0).toFixed(2)}`} color={(s.profit_per_day||0) >= 0 ? '#00ff88' : '#ff4757'} />
        <ModalStat label="Profit/Month" value={`$${(s.profit_per_month||0).toLocaleString(undefined,{minimumFractionDigits:2})}`} color={(s.profit_per_month||0) >= 0 ? '#00ff88' : '#ff4757'} />
        <ModalStat label="Trades/Day" value={(s.trades_per_day||0).toFixed(2)} color="var(--text-primary, #e6edf3)" />
      </div>

      {/* === DRAWDOWN ANALYSIS === */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
        <TrendingDown size={14} style={{ color: '#ff4757' }} />
        <span style={{ color: '#ff4757', fontSize: '13px', fontWeight: 600 }}>Drawdown Analysis</span>
        <div style={{ display: 'flex', background: 'var(--border-color, #21262d)', borderRadius: '4px', overflow: 'hidden' }}>
          <button onClick={() => setDdType('maximal')} style={{ padding: '3px 10px', fontSize: '10px', fontWeight: 600, border: 'none', cursor: 'pointer', background: ddType === 'maximal' ? '#378ADD' : 'transparent', color: ddType === 'maximal' ? '#fff' : 'var(--text-secondary, #8b949e)' }}>Maximal</button>
          <button onClick={() => setDdType('absolute')} style={{ padding: '3px 10px', fontSize: '10px', fontWeight: 600, border: 'none', cursor: 'pointer', background: ddType === 'absolute' ? '#378ADD' : 'transparent', color: ddType === 'absolute' ? '#fff' : 'var(--text-secondary, #8b949e)' }}>Absolute</button>
        </div>
      </div>
      <p style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '10px', marginBottom: '12px' }}>
        {ddType === 'maximal' ? 'Peak-to-trough equity decline (trade-by-trade)' : 'Decline below starting equity (trade-by-trade)'}
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
        <ModalStat label={ddType === 'maximal' ? 'Max Drawdown' : 'Absolute DD'} value={`$${(s.max_drawdown||0).toFixed(2)}`} sub={`${(s.max_drawdown_pct||0).toFixed(2)}%`} color="#ff4757" />
        <ModalStat label="Avg Drawdown" value={`$${(s.avg_drawdown||0).toFixed(2)}`} color="#ff4757" />
      </div>
      {/* DD Chart Toggle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
        <div style={{ display: 'flex', background: 'var(--border-color, #21262d)', borderRadius: '4px', overflow: 'hidden' }}>
          <button onClick={() => setChartMode('DD')} style={{ padding: '3px 10px', fontSize: '10px', fontWeight: 600, border: 'none', cursor: 'pointer', background: chartMode === 'DD' ? '#378ADD' : 'transparent', color: chartMode === 'DD' ? '#fff' : 'var(--text-secondary, #8b949e)' }}>DD</button>
          <button onClick={() => setChartMode('Float')} style={{ padding: '3px 10px', fontSize: '10px', fontWeight: 600, border: 'none', cursor: 'pointer', background: chartMode === 'Float' ? '#378ADD' : 'transparent', color: chartMode === 'Float' ? '#fff' : 'var(--text-secondary, #8b949e)' }}>Float</button>
        </div>
      </div>
      {chartData.length > 0 && <ChartCard data={chartData} color={chartMode === 'DD' ? '#ff4757' : '#00ff88'} />}

      {/* === FLOATING DD RECORDS === */}
      <FloatingDDSection data={data.floating_dd} />

      {/* === TRADING COSTS === */}
      <IconHeader icon={<DollarSign size={14} />} title="Trading Costs" color="#f59e0b" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px', marginBottom: '24px' }}>
        <ModalStat label="Total Swap" value={`${(costs.total_swap||0) >= 0 ? '+' : '-'}$${Math.abs(costs.total_swap||0).toFixed(2)}`} sub="Overnight" color={(costs.total_swap||0) >= 0 ? '#00ff88' : '#ff4757'} />
        <ModalStat label="Commission" value={`${(costs.total_commission||0) >= 0 ? '+' : '-'}$${Math.abs(costs.total_commission||0).toFixed(2)}`} sub="Broker fees" color="#f59e0b" />
        <ModalStat label="Total Costs" value={`${(costs.total_costs||0) >= 0 ? '+' : '-'}$${Math.abs(costs.total_costs||0).toFixed(2)}`} sub={`$${Math.abs(costs.cost_per_trade||0).toFixed(2)}/trade`} color="#ff4757" />
      </div>

      {/* === WIN/LOSS STREAK === */}
      <IconHeader icon={<Flame size={14} />} title="Win/Loss Streak Analysis" color="#f59e0b" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px', marginBottom: '24px' }}>
        <ModalStat label="Current Streak" value={Math.abs(s.current_streak||0)} sub={(s.current_streak||0) >= 0 ? 'Winning' : 'Losing'} color={(s.current_streak||0) >= 0 ? '#00ff88' : '#ff4757'} />
        <ModalStat label="Longest Win" value={s.longest_win_streak||0} color="#00ff88" />
        <ModalStat label="Longest Loss" value={s.longest_loss_streak||0} color="#ff4757" />
      </div>

      {/* === SESSION PERFORMANCE === */}
      <IconHeader icon={<Globe size={14} />} title="Session Performance" color="#00ff88" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px', marginBottom: '24px' }}>
        {Object.entries({ asian: { name: 'Asian', flag: '🇯🇵', time: '19:00-04:00 NY', color: '#f59e0b' }, london: { name: 'London', flag: '🇬🇧', time: '03:00-12:00 NY', color: '#00ff88' }, new_york: { name: 'New York', flag: '🇺🇸', time: '08:00-17:00 NY', color: '#a855f7' } }).map(([key, meta]) => {
          const d = sessionPerf[key] || { profit: 0, count: 0, win_rate: 0, profit_factor: 0 };
          return (
            <div key={key} style={{ background: 'var(--bg-card, #161b22)', border: '1px solid var(--border-color, #21262d)', borderRadius: '8px', padding: '14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                <span style={{ fontSize: '12px' }}>{meta.flag}</span>
                <span style={{ color: meta.color, fontSize: '12px', fontWeight: 700 }}>{meta.name}</span>
              </div>
              <p style={{ color: 'var(--text-dim, #484f58)', fontSize: '9px', marginBottom: '8px' }}>{meta.time}</p>
              <p style={{ color: d.profit >= 0 ? '#00ff88' : '#ff4757', fontSize: '20px', fontWeight: 700, margin: '0 0 6px' }}>{d.profit >= 0 ? '+' : ''}${Math.abs(d.profit).toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
              <p style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '10px', margin: 0 }}>{d.count} trades • {d.win_rate}% WR</p>
              <p style={{ color: 'var(--text-dim, #484f58)', fontSize: '9px', margin: '2px 0 0' }}>PF: {d.profit_factor}</p>
            </div>
          );
        })}
      </div>

      {/* === HOURLY PERFORMANCE === */}
      <IconHeader icon={<Clock size={14} />} title="Performance by Hour (NY Time)" color="#378ADD" />
      <div style={{ background: 'var(--bg-card, #161b22)', border: '1px solid var(--border-color, #21262d)', borderRadius: '8px', padding: '16px', marginBottom: '24px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(40px, 1fr))', gap: '3px' }}>
          {hourlyPerf.map(({ hour, profit, count }) => {
            const maxP = Math.max(...hourlyPerf.map(h => Math.abs(h.profit)), 1);
            const intensity = Math.min(1, Math.abs(profit) / maxP);
            const bg = count === 0 ? 'var(--bg-main, #0d1117)' : profit >= 0 ? `rgba(0,255,136,${0.15 + intensity * 0.5})` : `rgba(255,71,87,${0.15 + intensity * 0.5})`;
            return (
              <div key={hour} style={{ background: bg, borderRadius: '6px', padding: '4px 2px', textAlign: 'center' }}>
                <p style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '8px', margin: 0 }}>{hour}h</p>
                <p style={{ color: count === 0 ? 'var(--text-dim, #484f58)' : profit >= 0 ? '#00ff88' : '#ff4757', fontSize: '9px', fontWeight: 700, margin: '2px 0 0' }}>
                  {count === 0 ? '-' : `${profit >= 0 ? '+' : ''}${Math.round(profit)}`}
                </p>
                <p style={{ color: 'var(--text-dim, #484f58)', fontSize: '7px', margin: 0 }}>{count}t</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* === DAILY PERFORMANCE === */}
      <IconHeader icon={<Calendar size={14} />} title="Performance by Day" color="#a855f7" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: '6px', marginBottom: '24px' }}>
        {dailyPerf.map(({ day, profit, count, win_rate }) => (
          <div key={day} style={{ background: 'var(--bg-card, #161b22)', border: '1px solid var(--border-color, #21262d)', borderRadius: '10px', padding: '12px 8px', textAlign: 'center' }}>
            <p style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '10px', fontWeight: 600, margin: '0 0 6px' }}>{day}</p>
            <p style={{ color: count === 0 ? 'var(--text-dim, #484f58)' : profit >= 0 ? '#00ff88' : '#ff4757', fontSize: '16px', fontWeight: 700, margin: '0 0 4px' }}>
              {count === 0 ? '-' : `${profit >= 0 ? '+' : ''}$${Math.abs(profit).toFixed(0)}`}
            </p>
            <p style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '9px', margin: '0 0 2px' }}>{count} trades</p>
            <p style={{ color: win_rate >= 60 ? '#00ff88' : win_rate >= 50 ? '#f59e0b' : '#ff4757', fontSize: '9px', margin: 0 }}>{win_rate}% WR</p>
          </div>
        ))}
      </div>

      {/* === EQUITY CURVE === */}
      <IconHeader icon={<TrendingUp size={14} />} title="Equity Curve" color="#378ADD" />
      {equityCurve.length > 0 && <ChartCard data={equityCurve} dataKey="equity" color="#378ADD" />}

      {/* === MONTHLY HEATMAP === */}
      {monthlyHeatmap.length > 0 && (
        <>
          <IconHeader icon={<TrendingUp size={14} />} title="Monthly Performance Heatmap" color="#378ADD" />
          <MonthlyHeatmap data={monthlyHeatmap} />
        </>
      )}

      {/* === DAILY HEATMAP === */}
      {dailyHeatmap.length > 0 && (
        <>
          <IconHeader icon={<CalendarDays size={14} />} title="Daily Performance Heatmap" color="#378ADD" />
          <DailyHeatmap data={dailyHeatmap} />
        </>
      )}

      {/* === RISK METRICS === */}
      <IconHeader icon={<Scale size={14} />} title="Risk Metrics" color="#a855f7" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px', marginBottom: '24px' }}>
        <ModalStat label="Risk/Reward" value={(s.risk_reward||0).toFixed(2)} color="var(--text-primary, #e6edf3)" />
        <ModalStat label="Largest Win" value={`+$${(s.largest_win||0).toFixed(2)}`} color="#00ff88" />
        <ModalStat label="Largest Loss" value={`-$${Math.abs(s.largest_loss||0).toFixed(2)}`} color="#ff4757" />
        <ModalStat label="Avg Duration" value={fmtDur(s.avg_duration_seconds)} color="var(--text-primary, #e6edf3)" />
      </div>

      {/* Avg Win vs Loss */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', marginBottom: '24px' }}>
        <div style={{ background: 'var(--bg-card, #161b22)', border: '1px solid rgba(0,255,136,0.2)', borderRadius: '8px', padding: '14px' }}>
          <p style={{ color: '#00ff88', fontSize: '10px', marginBottom: '4px' }}>Average Win</p>
          <p style={{ color: '#00ff88', fontSize: '22px', fontWeight: 700, margin: 0 }}>+${(s.avg_win||0).toFixed(2)}</p>
          <p style={{ color: 'var(--text-dim, #484f58)', fontSize: '10px', marginTop: '4px' }}>{s.winning_trades||0} winning trades</p>
        </div>
        <div style={{ background: 'var(--bg-card, #161b22)', border: '1px solid rgba(255,71,87,0.2)', borderRadius: '8px', padding: '14px' }}>
          <p style={{ color: '#ff4757', fontSize: '10px', marginBottom: '4px' }}>Average Loss</p>
          <p style={{ color: '#ff4757', fontSize: '22px', fontWeight: 700, margin: 0 }}>-${Math.abs(s.avg_loss||0).toFixed(2)}</p>
          <p style={{ color: 'var(--text-dim, #484f58)', fontSize: '10px', marginTop: '4px' }}>{s.losing_trades||0} losing trades</p>
        </div>
      </div>

      {/* === SYMBOL BREAKDOWN === */}
      {symbols.length > 0 && (
        <>
          <IconHeader icon={<Coins size={14} />} title="Symbol Breakdown" color="#06b6d4" />
          <div style={{ background: 'var(--bg-card, #161b22)', border: '1px solid var(--border-color, #21262d)', borderRadius: '8px', padding: '14px', marginBottom: '24px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '8px' }}>
              {symbols.map(sym => (
                <div key={sym.symbol} style={{ background: 'var(--bg-main, #0d1117)', border: '1px solid var(--border-color, #21262d)', borderRadius: '6px', padding: '10px', textAlign: 'center' }}>
                  <p style={{ color: 'var(--text-primary, #e6edf3)', fontSize: '11px', fontWeight: 700, margin: '0 0 4px' }}>{sym.symbol}</p>
                  <p style={{ color: sym.profit >= 0 ? '#00ff88' : '#ff4757', fontSize: '14px', fontWeight: 700, margin: '0 0 2px' }}>{sym.profit >= 0 ? '+' : ''}${sym.profit.toFixed(2)}</p>
                  <p style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '9px', margin: 0 }}>{sym.count} trades • {sym.win_rate}% WR</p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* === OPEN TRADES === */}
      {openTrades.length > 0 && (
        <>
          <IconHeader icon={<Activity size={14} />} title={`Open Trades (${openTrades.length})`} color="#06b6d4" />
          <div style={{ background: 'var(--bg-card, #161b22)', border: '1px solid var(--border-color, #21262d)', borderRadius: '8px', padding: '14px', marginBottom: '24px', overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-color, #21262d)' }}>
                  <th style={{ color: 'var(--text-secondary, #8b949e)', padding: '6px 8px', textAlign: 'left' }}>Symbol</th>
                  <th style={{ color: 'var(--text-secondary, #8b949e)', padding: '6px 8px', textAlign: 'left' }}>Type</th>
                  <th style={{ color: 'var(--text-secondary, #8b949e)', padding: '6px 8px', textAlign: 'right' }}>Lots</th>
                  <th style={{ color: 'var(--text-secondary, #8b949e)', padding: '6px 8px', textAlign: 'right' }}>Profit</th>
                  <th style={{ color: 'var(--text-secondary, #8b949e)', padding: '6px 8px', textAlign: 'right' }}>Pips</th>
                </tr>
              </thead>
              <tbody>
                {openTrades.map((t, i) => (
                  <tr key={i}>
                    <td style={{ color: 'var(--text-primary, #e6edf3)', padding: '6px 8px' }}>{t.symbol}</td>
                    <td style={{ color: t.type === 'BUY' ? '#00ff88' : '#ff4757', padding: '6px 8px', fontWeight: 600 }}>{t.type}</td>
                    <td style={{ color: 'var(--text-primary, #e6edf3)', padding: '6px 8px', textAlign: 'right' }}>{t.lots}</td>
                    <td style={{ color: (t.profit||0) >= 0 ? '#00ff88' : '#ff4757', padding: '6px 8px', textAlign: 'right', fontWeight: 600 }}>{(t.profit||0) >= 0 ? '+' : ''}${(t.profit||0).toFixed(2)}</td>
                    <td style={{ color: (t.pips||0) >= 0 ? '#00ff88' : '#ff4757', padding: '6px 8px', textAlign: 'right' }}>{(t.pips||0).toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Total Closed Trades */}
      <div style={{ background: 'var(--bg-card, #161b22)', border: '1px solid var(--border-color, #21262d)', borderRadius: '8px', padding: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '12px' }}>Total Closed Trades: <strong style={{ color: 'var(--text-primary, #e6edf3)' }}>{totalClosedTrades}</strong></span>
      </div>
    </div>
  );
}

// === HELPER COMPONENTS (same as MyEAs internal ones) ===

function FloatingDDSection({ data }) {
  if (!data || Object.keys(data).length === 0) return null;
  const daily = data.daily || data.daily_record;
  const weekly = data.weekly || data.weekly_record;
  const monthly = data.monthly || data.monthly_record;
  const allTime = data.all_time || data.alltime_record;

  const records = [
    { label: 'Daily Max', data: daily },
    { label: 'Weekly Max', data: weekly },
    { label: 'Monthly Max', data: monthly },
    { label: 'All-time Max', data: allTime },
  ].filter(r => r.data && r.data.dd_amount);

  if (records.length === 0) return null;

  return (
    <div style={{ marginBottom: '24px' }}>
      <IconHeader icon={<AlertTriangle size={14} />} title="Floating Drawdown Records" color="#ff4757" />
      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px', marginBottom: '14px' }}>
        {[
          { label: 'DAILY RECORD', data: daily },
          { label: 'WEEKLY RECORD', data: weekly },
          { label: 'ALL TIME RECORD', data: allTime },
        ].filter(r => r.data).map(({ label, data: d }) => (
          <div key={label} style={{ background: 'var(--bg-card, #161b22)', border: '1px solid var(--border-color, #21262d)', borderRadius: '8px', padding: '14px', textAlign: 'center' }}>
            <p style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>{label}</p>
            <p style={{ color: '#ff4757', fontSize: '18px', fontWeight: 700, margin: '0 0 2px' }}>-${Math.abs(d.dd_amount).toLocaleString(undefined, {minimumFractionDigits:2})}</p>
            <p style={{ color: '#ff4757', fontSize: '10px', opacity: 0.7, margin: 0 }}>{d.dd_percent.toFixed(2)}%</p>
          </div>
        ))}
      </div>
      {/* Record List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
        {records.map(({ label, data: d }) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'var(--bg-card, #161b22)', border: '1px solid var(--border-color, #21262d)', borderRadius: '6px' }}>
            <div>
              <span style={{ color: 'var(--text-primary, #e6edf3)', fontSize: '12px', fontWeight: 600 }}>{label}</span>
              {d.recorded_at && <p style={{ color: 'var(--text-dim, #484f58)', fontSize: '10px', margin: '3px 0 0' }}>{new Date(d.recorded_at).toLocaleDateString()} • Peak: ${d.peak_equity?.toLocaleString(undefined, {minimumFractionDigits:2}) || '0.00'}</p>}
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ color: '#ff4757', fontSize: '13px', fontWeight: 700, margin: 0 }}>-${Math.abs(d.dd_amount).toLocaleString(undefined, {minimumFractionDigits:2})}</p>
              <p style={{ color: '#ff4757', fontSize: '10px', opacity: 0.7, margin: '2px 0 0' }}>{d.dd_percent.toFixed(2)}%</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ModalStat({ label, value, sub, color }) {
  return (
    <div style={{ background: 'var(--bg-card, #161b22)', border: '1px solid var(--border-color, #21262d)', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
      <p style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '9px', textTransform: 'uppercase', marginBottom: '4px' }}>{label}</p>
      <p style={{ color, fontSize: '16px', fontWeight: 700, margin: 0 }}>{value}</p>
      {sub && <p style={{ color: 'var(--text-dim, #484f58)', fontSize: '9px', margin: '2px 0 0' }}>{sub}</p>}
    </div>
  );
}

function SectionHeader({ title }) {
  return <p style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '12px', fontWeight: 600, marginBottom: '10px' }}>{title}</p>;
}

function IconHeader({ icon, title, color }) {
  return <p style={{ color, fontSize: '13px', fontWeight: 600, marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>{icon} {title}</p>;
}

function ChartCard({ data, dataKey = 'value', color }) {
  return (
    <div style={{ background: 'var(--bg-card, #161b22)', border: '1px solid var(--border-color, #21262d)', borderRadius: '8px', padding: '12px', marginBottom: '24px' }}>
      <ResponsiveContainer width="100%" height={170}>
        <AreaChart data={data}>
          <defs>
            <linearGradient id={`sg-${color}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={color} stopOpacity={0.3} />
              <stop offset="95%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color, #21262d)" />
          <XAxis dataKey={dataKey === 'equity' ? 'x' : 'date'} stroke="var(--text-secondary, #8b949e)" tick={{ fill: 'var(--text-secondary, #8b949e)', fontSize: 9 }} />
          <YAxis stroke="var(--text-secondary, #8b949e)" tick={{ fill: 'var(--text-secondary, #8b949e)', fontSize: 9 }} />
          <Tooltip contentStyle={{ backgroundColor: 'var(--bg-card, #161b22)', border: '1px solid var(--border-color, #21262d)', borderRadius: '8px' }} />
          <Area type="monotone" dataKey={dataKey} stroke={color} fill={`url(#sg-${color})`} strokeWidth={1.5} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function MonthlyHeatmap({ data }) {
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const years = [...new Set(data.map(d => d.year))].sort();
  const lookup = {};
  data.forEach(d => { lookup[`${d.year}-${d.month}`] = d.profit; });
  return (
    <div style={{ background: 'var(--bg-card, #161b22)', border: '1px solid var(--border-color, #21262d)', borderRadius: '8px', padding: '16px', marginBottom: '24px', overflowX: 'auto' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '40px repeat(12, 1fr)', gap: '3px', alignItems: 'center' }}>
        <span style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '10px' }}>Year</span>
        {months.map(m => <span key={m} style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '9px', textAlign: 'center' }}>{m}</span>)}
        {years.map(year => (
          <React.Fragment key={year}>
            <span style={{ color: 'var(--text-primary, #e6edf3)', fontSize: '12px', fontWeight: 700 }}>{year}</span>
            {Array.from({ length: 12 }, (_, i) => {
              const profit = lookup[`${year}-${i + 1}`];
              if (profit === undefined) return <span key={i} style={{ textAlign: 'center', color: 'var(--text-dim, #484f58)', fontSize: '10px' }}>—</span>;
              const bg = profit > 0 ? '#1a7a4a' : '#7a3030';
              return (
                <div key={i} style={{ background: bg, borderRadius: '4px', padding: '4px 2px', textAlign: 'center' }}>
                  <span style={{ color: '#fff', fontSize: '9px', fontWeight: 700 }}>{profit > 0 ? '+' : ''}{Math.round(profit)}</span>
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function DailyHeatmap({ data }) {
  const dailyMap = {};
  data.forEach(d => { dailyMap[d.date] = d.profit; });
  const monthSet = new Set();
  Object.keys(dailyMap).forEach(d => { monthSet.add(d.substring(0, 7)); });
  const sortedMonths = [...monthSet].sort().reverse().slice(0, 4);
  if (sortedMonths.length === 0) return null;
  const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(sortedMonths.length, 2)}, 1fr)`, gap: '12px', marginBottom: '24px' }}>
      {sortedMonths.map(ym => {
        const [y, m] = ym.split('-').map(Number);
        const firstDay = new Date(y, m - 1, 1).getDay();
        const daysInMonth = new Date(y, m, 0).getDate();
        return (
          <div key={ym} style={{ background: 'var(--bg-main, #0d1117)', border: '1px solid var(--border-color, #21262d)', borderRadius: '10px', padding: '14px' }}>
            <span style={{ color: 'var(--text-primary, #e6edf3)', fontSize: '13px', fontWeight: 700, display: 'block', marginBottom: '10px' }}>{monthNames[m-1]} {y}</span>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
              {Array.from({ length: firstDay }).map((_, i) => <div key={`e${i}`} style={{ aspectRatio: '1' }} />)}
              {Array.from({ length: daysInMonth }, (_, i) => {
                const day = i + 1;
                const key = `${y}-${String(m).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
                const profit = dailyMap[key];
                const has = profit !== undefined && profit !== 0;
                let bg = 'var(--bg-card, #161b22)';
                if (has) bg = profit > 0 ? 'rgba(0,255,136,0.15)' : 'rgba(255,71,87,0.15)';
                return (
                  <div key={day} style={{ aspectRatio: '1', borderRadius: '4px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: bg, minHeight: '28px' }}>
                    <span style={{ fontSize: '8px', color: 'var(--text-secondary, #8b949e)' }}>{day}</span>
                    {has && <span style={{ fontSize: '7px', fontWeight: 700, color: profit >= 0 ? '#00ff88' : '#ff4757' }}>{Math.round(profit)}</span>}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function LiveReadinessVerdictShared({ stats, totalClosedTrades }) {
  const s = stats;
  const criteria = [];
  let totalScore = 0, maxScore = 0;

  const tc = totalClosedTrades || s.total_trades || 0;
  let ts = tc >= 200 ? 10 : tc >= 100 ? 7 : tc >= 50 ? 4 : 1;
  criteria.push({ name: 'Sample Size', score: ts, max: 10 }); totalScore += ts; maxScore += 10;

  const pf = s.profit_factor || 0;
  let ps = pf >= 2 ? 10 : pf >= 1.5 ? 8 : pf >= 1.2 ? 5 : pf >= 1 ? 2 : 0;
  criteria.push({ name: 'Profit Factor', score: ps, max: 10 }); totalScore += ps; maxScore += 10;

  const wr = s.win_rate || 0, exp = s.expectancy || 0;
  let ws = exp > 0 && wr >= 55 ? 9 : exp > 0 && wr >= 40 ? 7 : exp > 0 ? 5 : exp === 0 ? 2 : 0;
  criteria.push({ name: 'Win Rate', score: ws, max: 10 }); totalScore += ws; maxScore += 10;

  const dd = s.max_drawdown_pct || 0;
  let ds = dd <= 10 ? 10 : dd <= 20 ? 8 : dd <= 35 ? 5 : dd <= 50 ? 2 : 0;
  criteria.push({ name: 'Drawdown', score: ds, max: 10 }); totalScore += ds; maxScore += 10;

  const pct = maxScore > 0 ? Math.round(totalScore / maxScore * 100) : 0;
  let verdict, verdictColor, verdictBg;
  if (pct >= 75) { verdict = 'READY FOR LIVE'; verdictColor = '#00ff88'; verdictBg = 'rgba(0,255,136,0.08)'; }
  else if (pct >= 55) { verdict = 'CAUTIOUS'; verdictColor = '#f59e0b'; verdictBg = 'rgba(245,158,11,0.08)'; }
  else if (pct >= 35) { verdict = 'NEEDS MORE TESTING'; verdictColor = '#f97316'; verdictBg = 'rgba(249,115,22,0.08)'; }
  else { verdict = 'NOT RECOMMENDED'; verdictColor = '#ff4757'; verdictBg = 'rgba(255,71,87,0.08)'; }

  return (
    <div style={{ background: verdictBg, border: `1px solid ${verdictColor}30`, borderRadius: '12px', padding: '16px', marginBottom: '24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <p style={{ color: verdictColor, fontSize: '14px', fontWeight: 700, margin: 0 }}>{verdict}</p>
          <p style={{ color: 'var(--text-secondary, #8b949e)', fontSize: '10px', margin: '2px 0 0' }}>Live Trading Readiness Score</p>
        </div>
        <p style={{ color: verdictColor, fontSize: '28px', fontWeight: 700, margin: 0 }}>{pct}%</p>
      </div>
      <div style={{ height: '6px', background: 'var(--border-color, #21262d)', borderRadius: '3px', marginTop: '12px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: verdictColor, borderRadius: '3px' }} />
      </div>
    </div>
  );
}

function fmtDur(seconds) {
  if (!seconds) return '-';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}
